import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'printTemplate',
    'calculatorContainer'
  ]

  hasCustomIndex = false
  marketRates = null

  handlePrint(event) {
    event.preventDefault()
    if (!this.hasPrintTemplateTarget) return

    this.marketRates = this._getMarketRates()
    this.hasCustomIndex = this.calculatorContainerTarget.querySelector('[data-print*="custom-index"]')?.classList.contains('d-none') ? false : true

    const data = {
      pages: [
        {
          title: 'Vessel Comparison Report - Summary',
          content: this._getPage1Content()
        },
        {
          title: 'Analysis - Index',
          content: this._getPage2Content()
        },
        {
          title: 'Analysis - Speeds',
          content: this._getPage3Content()
        },
        {
          title: 'Vessel Features',
          content: this._getPage4Content()
        },
      ]
    }

    this.printTemplateTarget.dispatchEvent(new CustomEvent('print-template', { detail: data }))
  }

  //#region Page content methods
  _getPage1Content() {
    const content = this.calculatorContainerTarget.cloneNode(true)

    this._removeOtherGroups(content)
    this._showOnlyFirst4Vessels(content)

    const calculatorSections = this._getSections(content)

    // toggle all calculator sections to show only the vessel particulars and speeds consumption
    const sectionsToShow = ['vessel-particulars', 'speeds-consumption']
    this._toggleCollapseSections(calculatorSections, sectionsToShow)

    this._hideElements(content, [
      'vessel-features',
      'ghg',
      'eexi'
    ])

    return `
      <div class="calculator-print-page">
        ${content.outerHTML}
        ${this.marketRates.outerHTML}
      </div>
    `
  }
  _getPage2Content() {
    const content = this.calculatorContainerTarget.cloneNode(true)

    this._removeOtherGroups(content)
    this._showOnlyFirst4Vessels(content)

    const calculatorSections = this._getSections(content)

    // expand VESSELINDEX® section
    const sectionsToShow = ['vessel-index', 'sisterships-index', 'average-index']
    if (this.hasCustomIndex) {
      sectionsToShow.push('custom-index', 'average-custom-index')
    }
    this._toggleCollapseSections(calculatorSections, sectionsToShow)

    // hide voyages and show speeds
    this._showElements(content, [
      'vessel-index',
      'sisterships-index',
      'optispeed-voyages',
      'voyages-labels',
      'average-index'
    ])
    if (this.hasCustomIndex) {
      this._showElements(content, [
        'custom-index',
        'average-custom-index'
      ])
    }

    // Hide all other sections
    this._hideElements(content, ['optispeed-speed',
      'speed-labels',
      'vessel-particulars',
      'vessel-features',
      'speeds-consumption',
      'button-sections',
      'ghg',
      'eexi',
      'singlespeed-index',
    ])

    return `
      <div class="calculator-print-page">
        ${content.outerHTML}
        ${this.marketRates.outerHTML}
      </div>
    `
  }
  _getPage3Content() {
    const content = this.calculatorContainerTarget.cloneNode(true)

    this._removeOtherGroups(content)
    this._showOnlyFirst4Vessels(content)

    // expand VESSELINDEX® section
    const calculatorSections = this._getSections(content)
    const sectionsToShow = ['vessel-index', 'sisterships-index', 'average-index']
    if (this.hasCustomIndex) {
      sectionsToShow.push('custom-index', 'average-custom-index')
    }
    this._toggleCollapseSections(calculatorSections, sectionsToShow)

    // hide voyages and show labels
    this._showElements(content, [
      'vessel-index',
      'sisterships-index',
      'optispeed-speed',
      'speed-labels',
      'average-index'
    ])
    if (this.hasCustomIndex) {
      this._showElements(content, [
        'custom-index',
        'average-custom-index'
      ])
    }

    // Hide all other sections
    this._hideElements(content, ['optispeed-voyages',
      'voyages-labels',
      'vessel-particulars',
      'vessel-features',
      'speeds-consumption',
      'button-sections',
      'ghg',
      'eexi',
      'singlespeed-index'
    ])

    return `
      <div class="calculator-print-page">
        ${content.outerHTML}
        ${this.marketRates.outerHTML}
      </div>
    `
  }
  _getPage4Content() {
    const content = this.calculatorContainerTarget.cloneNode(true)

    this._removeOtherGroups(content)
    this._showOnlyFirst4Vessels(content)

    // expand Vessel Features section
    const calculatorSections = this._getSections(content)
    const sectionsToShow = ['vessel-features']
    this._toggleCollapseSections(calculatorSections, sectionsToShow)

    // Hide all other sections
    this._hideElements(content, [
      'vessel-particulars',
      'speeds-consumption',
      'button-sections',
      'vessel-index',
      'sisterships-index',
      'custom-index',
      'ghg',
      'eexi'
    ])

    return `
      <div class="calculator-print-page">
        ${content.outerHTML}
      </div>
    `
  }
  //#endregion Page content methods

  //#region Helper methods
  _getMarketRates() {
    const marketRatesCopy = document.querySelector('[data-print*="market-rates"]').cloneNode(true)

    const marketRatesSlot = Array.from(this.printTemplateTarget.content.children).find(t => t.dataset.role === 'content').content.querySelector('[data-print*="market-rates-slot"]')
    marketRatesSlot.innerHTML = ''
    marketRatesSlot.appendChild(marketRatesCopy)

    // Replace all number inputs with their values, otherwise it will show outdated values
    const inputs = marketRatesSlot.querySelectorAll('input[type="number"]')
    inputs.forEach(input => {
      input.outerHTML = input.value
    })

    return marketRatesSlot.cloneNode(true)
  }
  _getSections(container) {
    const sections = container.querySelectorAll('[data-print*="calculator-section"]')
    return sections
  }
  _toggleCollapseSections(sections, sectionsToShow) {
    sections.forEach(section => {
      const dataPrintValues = section.dataset.print.split(' ')
      const isSectionToShow = sectionsToShow.some(sectionToShow => dataPrintValues.includes(sectionToShow))
      section.classList.toggle('show', isSectionToShow)
    })
  }
  _showElements(container, elements) {
    this._toggleElementsVisibility(container, elements, 'show')
  }
  _hideElements(container, elements) {
    this._toggleElementsVisibility(container, elements, 'hide')
  }
  _toggleElementsVisibility(container, elements, action = 'show') {
    if (!container || !elements) return
    elements.forEach(element => {
      const elements = container.querySelectorAll(`[data-print*="${element}"]`)
      elements.forEach(element => {
        element.style.display = action === 'show' ? 'block' : 'none'

        if (action === 'show') {
          element.classList.remove('d-none')
        }
      })
    })
  }
  _showOnlyFirst4Vessels(container) {
    if (!container) return container

    Array.from(container.querySelectorAll('[data-print*="calc-vessel"]'))?.forEach((vessel, index) => {
      if ((index + 1) > 4) {
        const vesselForm = vessel.closest('[data-print*="vessel-form"]')
        if (vesselForm) {
          vesselForm.remove()
        }
      }
    })
  }
  _removeOtherGroups(container) {
    if (!container) return container

    Array.from(container.querySelectorAll('[data-print*="panel"]:not(.is-active)'))?.forEach((group, index) => {
      group.remove()
    })

  }
  //#endregion Helper methods
}